import { ComponentType } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, effect, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { GuaranteeStatus, SimilarPrescriptionsService, deletePrescriptionDraft } from '@lib/prescriptions/data-access';
import {
  PersonalDoctorMetadata,
  PharmacologicalItemMetaData,
  Prescription,
  PrescriptionCategory,
  PrescriptionPlanning,
  PrescriptionPlanningStatus,
  PrescriptionStatus,
  PrescriptionTypes,
  ResponseMorionItem,
} from '@lib/prescriptions/data-access';
import { EDIT_BLOCKING_PLANNING_STATUSES, GUARANTEE_REASON_UNKNOWN, NO_ACHI_CODE } from '@lib/prescriptions/data-access';
import { AppointmentsHttpService, PrescriptionsService } from '@lib/prescriptions/data-access';
import { refreshPrescriptionsData, savePrescription, selectPrescriptionsFilters, selectTargetPrescription } from '@lib/prescriptions/data-access';
import { combineReasons, getActualPlanning, getMedicationFullName, parseReason, prescriptionToRequestBody } from '@lib/prescriptions/data-access';
import {
  AppointmentsDetailsModalComponent,
  CancelAppointmentModalComponent,
  CancelAppointmentModalData,
  CancelLaboratoryOrderModalComponent,
  CancelLaboratoryOrderModalData,
  CreateAppointmentModalComponent,
  CreateAppointmentModalData,
  CreateLaboratoryOrderModalComponent,
  CreateLaboratoryOrderModalData,
  DeletePrescriptionModalComponent,
  FreeFormModalComponent,
  MedicationPrescriptionFormModalComponent,
  PersonalDoctorPrescriptionFormModalComponent,
  PrescriptionChangesListComponent,
  PrescriptionDialogComponent,
  PrescriptionIconComponent,
  PrescriptionPeriodComponent,
  PrescriptionStatusComponent,
  ServicePrescriptionFormModalComponent,
  SimilarPrescriptionsComponent,
} from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { of, switchMap, take, tap } from 'rxjs';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { AppointmentEventPlaceType, CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { LimitRule } from '@lib/core/models/insurance-package.interface';
import { LiloDrugSearchResponse, MedicationBasketRequest } from '@lib/core/models/protocol.interface';
import { AppointmentStatus, AppointmentTypes } from '@lib/core/models/visit.interface';
import { DoctorsService } from '@lib/core/services/doctors.service';

import { InfoBadgeColors, InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { LimitComponent } from '@lib/shared-ui/commons/limit/limit.component';
import { ListItemExpandedTemplateComponent } from '@lib/shared-ui/commons/list-item-expanded-template/list-item-expanded-template.component';
import { ListItemNeutralButtonComponent } from '@lib/shared-ui/commons/list-item-neutral-button/list-item-neutral-button.component';
import { PharmaciesCountComponent } from '@lib/shared-ui/commons/pharmacies-count/pharmacies-count.component';
import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { WarningMessageComponent } from '@lib/shared-ui/commons/warning-message/warning-message.component';

import type { MultiStepperModalData } from '../../../components/multi-stepper-modal/multi-stepper-modal.component';
import { MULTI_STEPPER_SCOPE } from '../../../components/multi-stepper-modal/multi-stepper-scope.token';
import { PHARMACY_ORDER_ENABLED } from '../../../tokens/pharmacy-order.token';

// Pharmacy search constants - these should be configurable in the future
const PHARMACIES_LATITUDE = 50.4501;
const PHARMACIES_LONGITUDE = 30.5234;
const PHARMACIES_MAX_SEARCH_DISTANCE_METERS = 5000;

@Component({
  selector: 'prescriptions-item',
  standalone: true,
  imports: [
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    LimitComponent,
    PharmaciesCountComponent,
    SpinLoadingComponent,
    InfoBadgeComponent,
    MatTooltip,
    MatCheckbox,
    TranslatePipe,
    WarningMessageComponent,
    ListItemExpandedTemplateComponent,
    ListItemNeutralButtonComponent,
    PrescriptionIconComponent,
    PrescriptionPeriodComponent,
    PrescriptionStatusComponent,
    PrescriptionChangesListComponent,
    SimilarPrescriptionsComponent,
  ],
  templateUrl: './prescription-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionItemComponent implements AfterViewInit {
  private readonly dialog = inject(MatDialog);
  private readonly prescriptionsService = inject(PrescriptionsService, { optional: true });
  private readonly doctorsService = inject(DoctorsService, { optional: true });
  private readonly createAppointmentService = inject(AppointmentsHttpService, { optional: true });
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly similarPrescriptionsService = inject(SimilarPrescriptionsService);
  private readonly isInMultiStepperScope = inject(MULTI_STEPPER_SCOPE, { optional: true }) ?? false;
  private readonly isPharmacyOrderEnabled = inject(PHARMACY_ORDER_ENABLED, { optional: true }) ?? false;

  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly guaranteeStatus = GuaranteeStatus;
  protected readonly planningStatus = PrescriptionPlanningStatus;
  protected readonly formatDate = formatDate;
  protected readonly colors = InfoBadgeColors;
  protected readonly noAchiCode = NO_ACHI_CODE;

  @ViewChild('prescriptionElement', { read: ElementRef, static: true }) prescriptionElement!: ElementRef<HTMLElement>;

  prescription = input.required<Prescription>();
  allPrescriptions = input<Prescription[]>([]);
  memberId = input<string>();
  selectable = input<boolean>(false);
  selected = input<boolean>(false);
  selectionEnabled = input<boolean>(true);
  showPrescriptionActions = input<boolean>(true); // Show actions menu and quick guarantee buttons by default
  checkSimilarSignal = input<number>(0); // Incremented by a parent to trigger the similar-prescriptions check in bulk
  guaranteeAwaitingProcessing = input<boolean>(false);

  prescriptionUpdated = output<boolean>();
  selectionToggled = output<{ prescription: Prescription; selected: boolean }>();

  protected readonly filter = this.store.selectSignal(selectPrescriptionsFilters);
  protected readonly targetPrescription = this.store.selectSignal(selectTargetPrescription);

  protected readonly doctorName = signal<string>(null);
  protected readonly loadingPharmaciesCount = signal<boolean>(false);
  protected readonly pharmaciesCount = signal<number>(null);
  protected readonly loadingLimits = signal<boolean>(false);
  protected readonly limits = signal<LimitRule[]>(null);
  protected readonly showHistoricalChanges = signal<boolean>(false);
  protected readonly showSimilarPrescriptions = signal<boolean>(false);
  protected readonly similarPrescriptions = signal<Prescription[]>([]);
  protected readonly similarLoading = signal<boolean>(false);
  protected readonly similarChecked = signal<boolean>(false);
  protected readonly hasSimilar = computed<boolean>(() => this.similarPrescriptions().length > 0);
  protected readonly itemLoading = signal<boolean>(false);

  protected readonly highlighted = computed<boolean>(() => {
    const searchHighlight = this.filter().search && this.prescription().name?.toUpperCase().includes(this.filter().search.trim().toUpperCase());
    const targetHighlight = this.targetPrescription()?.prescriptionId === this.prescription().id;

    return searchHighlight || targetHighlight;
  });
  protected readonly mappingInvalid = computed<boolean>(() => {
    const prescription = this.prescription();

    if (prescription.type === PrescriptionTypes.Drugs) {
      const meta = prescription.metadata as PharmacologicalItemMetaData;
      const innMissing = !meta?.inn || meta.inn.trim().length === 0;
      const formMissing = !meta?.form || meta.form.trim().length === 0;
      const morionRaw = (meta?.morion_items ?? meta?.trademarks)?.trim();
      const morionMissing = !morionRaw || morionRaw === '[]';

      return innMissing || formMissing || morionMissing;
    }

    if (
      prescription.type === PrescriptionTypes.Appointment ||
      prescription.type === PrescriptionTypes.InstrumentalDiagnostic ||
      prescription.type === PrescriptionTypes.LaboratoryService
    )
      return !prescription.metadata?.['code'];

    return false;
  });
  protected readonly trademarksIdentifierMismatch = computed<boolean>(() => {
    const prescription = this.prescription();
    if (prescription.type !== PrescriptionTypes.Drugs) return false;

    // Saved prescriptions carry the identifier on morion_items (`Identifier`); drafts on trademarks (`identifier`).
    const meta = prescription.metadata as PharmacologicalItemMetaData;
    const items: Array<ResponseMorionItem | LiloDrugSearchResponse> = JSON.parse(meta?.morion_items ?? meta?.trademarks ?? '[]');
    const identifiers = items.map(item => (item as ResponseMorionItem).Identifier ?? (item as LiloDrugSearchResponse).identifier);

    // Ignore unless there are 2+ items each carrying an identifier (old prescriptions predate the field); then flag if they differ.
    if (identifiers.length < 2 || identifiers.some(id => !id?.trim())) return false;

    return new Set(identifiers).size > 1;
  });
  protected readonly historicalChangesExist = computed<boolean>(() => this.prescription().historicalChanges?.length > 0);
  protected readonly showLimitsInfo = computed<boolean>(
    () =>
      !!this.prescription().guaranteeApplicationId &&
      (this.prescription().type === this.prescriptionTypes.Appointment || this.prescription().type === this.prescriptionTypes.InstrumentalDiagnostic)
  );

  protected readonly isEditEnabled = computed<boolean>(() => {
    if (this.isInMultiStepperScope) return false;
    if (this.prescription().isFreeForm) return true;
    if (!this.prescription().guaranteeApplicationId) return true;
    else if (this.prescription().type === PrescriptionTypes.PersonalDoctorAppointment) return false;
    else return !this.hasBlockingPlanning(this.prescription());
  });

  protected readonly showCreateGuaranteeFlow = computed<boolean>(() => {
    if (this.isInMultiStepperScope || this.prescription().isFreeForm || this.prescription().type === PrescriptionTypes.Drugs) return false;
    const hasCompletedPlanning = this.actualPrescriptionPlanning()?.planningStatus === PrescriptionPlanningStatus.Completed;
    const hasNoPlanning = !this.actualPrescriptionPlanning() || this.prescription().planningStatus === PrescriptionPlanningStatus.NotPlanned;

    return hasCompletedPlanning || hasNoPlanning;
  });

  protected readonly createGuaranteeFlowLabel = computed<string>(() =>
    this.actualPrescriptionPlanning()?.planningStatus === PrescriptionPlanningStatus.Completed ? 'guarantee.action.createGuarantee' : 'prescription.action.createAppointment'
  );
  protected readonly showManualAppointmentCreation = computed<boolean>(() => {
    if (this.isInMultiStepperScope || this.prescription().isFreeForm) return false;

    const prescription = this.prescription();
    const isSupportedType =
      prescription.type === this.prescriptionTypes.Appointment ||
      prescription.type === this.prescriptionTypes.InstrumentalDiagnostic ||
      prescription.type === this.prescriptionTypes.LaboratoryService;

    if (!isSupportedType) return false;

    const hasCapacity = (prescription.used ?? 0) < (prescription.count ?? 1);
    const isGuaranteed = prescription.guaranteeStatus === this.guaranteeStatus.Guaranteed || prescription.guaranteeStatus === this.guaranteeStatus.Auto;
    const isUnplanned = prescription.planningStatus === PrescriptionPlanningStatus.NotPlanned || prescription.planningStatus === PrescriptionPlanningStatus.Cancelled;

    return hasCapacity && (isGuaranteed || isUnplanned);
  });
  protected readonly showPharmacyOrderCreation = computed<boolean>(() => {
    if (!this.isPharmacyOrderEnabled || this.isInMultiStepperScope) return false;

    const prescription = this.prescription();
    if (prescription.type !== this.prescriptionTypes.Drugs) return false;

    const hasCapacity = (prescription.used ?? 0) < (prescription.count ?? 1);
    const isGuaranteed = prescription.guaranteeStatus === this.guaranteeStatus.Guaranteed || prescription.guaranteeStatus === this.guaranteeStatus.Auto;
    const isUnplanned = prescription.planningStatus === PrescriptionPlanningStatus.NotPlanned || prescription.planningStatus === PrescriptionPlanningStatus.Cancelled;

    return hasCapacity && (isGuaranteed || isUnplanned);
  });
  protected readonly showAppointmentCancel = computed<boolean>(() => !!this.findCancellablePlanning());
  protected readonly showAppointmentBinding = computed<boolean>(() => this.showManualAppointmentCreation() && !!this.prescription().guaranteeApplicationId);
  protected readonly isBindingDisabled = computed<boolean>(() => this.prescription().type === PrescriptionTypes.LaboratoryService);
  protected readonly statusReason = computed<string>(() => parseReason(this.prescription().reason).statusReason || GUARANTEE_REASON_UNKNOWN);
  protected readonly actualPrescriptionPlanning = computed<PrescriptionPlanning | null>(() => getActualPlanning(this.prescription()));
  protected readonly showAppointmentsDetails = computed<boolean>(() => !this.prescription().isFreeForm && !!this.actualPrescriptionPlanning());
  protected readonly showCompleteAppointment = computed<boolean>(
    () =>
      this.prescription().count === 1 &&
      !!this.actualPrescriptionPlanning() &&
      this.actualPrescriptionPlanning()!.planningStatus === PrescriptionPlanningStatus.Planned &&
      this.prescription().type !== PrescriptionTypes.LaboratoryService
  );
  protected readonly showCompleteLaboratoryOrder = computed<boolean>(
    () =>
      this.prescription().count === 1 &&
      !!this.actualPrescriptionPlanning() &&
      this.actualPrescriptionPlanning()!.appointmentId &&
      this.actualPrescriptionPlanning()!.planningStatus === PrescriptionPlanningStatus.Planned &&
      this.prescription().type === PrescriptionTypes.LaboratoryService
  );
  protected readonly medicationFullName = computed<string>(() => getMedicationFullName(this.prescription().metadata as PharmacologicalItemMetaData));
  protected readonly showPrescriptionDelete = computed<boolean>(() => {
    const prescription = this.prescription();
    if (prescription.isFreeForm) return true;

    const status = prescription.guaranteeStatus;
    if (status === GuaranteeStatus.Unknown || status === GuaranteeStatus.NotGuaranteed) return true;
    if (status === GuaranteeStatus.Guaranteed || status === GuaranteeStatus.Auto) return this.guaranteeAwaitingProcessing() && !prescription.planning?.length;

    return false;
  });

  protected readonly hasActionIndicator = computed<boolean>(() => {
    if (this.prescription().isFreeForm) return false;

    const planning = this.prescription().planning;
    if (!planning || planning.length === 0) return false;

    return planning.some(p => p.planningStatus === PrescriptionPlanningStatus.Planned || p.planningStatus === PrescriptionPlanningStatus.Completed);
  });

  protected readonly isPlanned = computed<boolean>(() => {
    const planning = this.prescription().planning;
    if (!planning || planning.length === 0) return false;

    return planning.some(p => p.planningStatus === PrescriptionPlanningStatus.Planned);
  });

  protected readonly isCompleted = computed<boolean>(() => {
    const planning = this.prescription().planning;
    if (!planning || planning.length === 0) return false;

    return planning.some(p => p.planningStatus === PrescriptionPlanningStatus.Completed);
  });

  protected toggleSelection() {
    if (!this.selectable() || !this.selectionEnabled()) return;
    this.selectionToggled.emit({
      prescription: this.prescription(),
      selected: !this.selected(),
    });
  }

  constructor() {
    effect(() => {
      if (this.checkSimilarSignal() > 0) this.checkSimilar();
    });

    effect(() => {
      const targetHighlight = this.targetPrescription()?.prescriptionId === this.prescription().id;

      if (targetHighlight && this.prescriptionElement?.nativeElement) {
        setTimeout(() => {
          this.prescriptionElement.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 100); // Small delay to ensure DOM is ready
      }
    });
  }

  ngAfterViewInit() {
    this.initPrescriptionItem();
  }

  private hasBlockingPlanning(prescription: Prescription): boolean {
    const planning = prescription.planning;
    if (!planning?.length) return false;

    const isLaboratory = prescription.type === PrescriptionTypes.LaboratoryService;
    const groupKey: keyof PrescriptionPlanning = isLaboratory ? 'orderId' : 'appointmentId';
    const latestMap = new Map<string, PrescriptionPlanning>();

    planning.forEach(p => {
      const key = (p[groupKey] as string) || p.id;
      const existing = latestMap.get(key);
      if (!existing || new Date(p.lastUpdateDate) > new Date(existing.lastUpdateDate)) {
        latestMap.set(key, p);
      }
    });

    return [...latestMap.values()].some(p => EDIT_BLOCKING_PLANNING_STATUSES.has(p.planningStatus));
  }

  private initPrescriptionItem() {
    switch (this.prescription().type) {
      case this.prescriptionTypes.PersonalDoctorAppointment:
        return this.getPersonalDoctorName();
    }
  }

  protected getLimitsRules() {
    if (this.limits()) return;
    this.loadingLimits.set(true);

    // TODO: This method might need to be provided via a separate service
    if (this.prescriptionsService && 'getPrescriptionLimits' in this.prescriptionsService) {
      (this.prescriptionsService as any)
        .getPrescriptionLimits(this.memberId(), this.prescription().guaranteeApplicationId, this.prescription().id)
        .pipe(take(1))
        .subscribe({
          next: (rules: any) => {
            this.limits.set(rules);
            this.loadingLimits.set(false);
          },
          error: () => {
            this.loadingLimits.set(false);
            console.error(`Encountered problems during limits fetch for ${this.prescription().name}.`);
          },
        });
    } else this.loadingLimits.set(false);
  }

  protected getPharmaciesCount(prescription: Prescription) {
    if (this.pharmaciesCount()) return;
    this.loadingPharmaciesCount.set(true);

    this.prescriptionsService
      .getMedicationBasket(this.createMedicationBasketRequest(prescription))
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.pharmacies?.length >= 0) this.pharmaciesCount.set(response.pharmacies.length);
          else console.error(`No pharmacies were found for ${prescription.name} in ${PHARMACIES_MAX_SEARCH_DISTANCE_METERS} meters.`);
          this.loadingPharmaciesCount.set(false);
        },
        error: () => {
          this.loadingPharmaciesCount.set(false);
          console.error(`Encountered problems during pharmacies search for ${prescription.name} in ${PHARMACIES_MAX_SEARCH_DISTANCE_METERS} meters.`);
        },
      });
  }

  private createMedicationBasketRequest(prescription: Prescription) {
    const medicationMetadata = prescription.metadata as PharmacologicalItemMetaData;
    const prescriptionId = crypto.randomUUID();

    if (this.prescription().guaranteeApplicationId) {
      const morion_items: ResponseMorionItem[] = JSON.parse(medicationMetadata.morion_items);
      const requestBody: MedicationBasketRequest = {
        uniqueId: crypto.randomUUID(),
        products: morion_items.map(item => {
          return {
            prescriptionId: prescription.id,
            code: item.Code.toString(),
            identifier: medicationMetadata.morion_substance || medicationMetadata.inn_ua || medicationMetadata.inn,
            substanceQuantity: item.SubsctanceQuantity,
            quantityInPackage: item.QuantityInPackage ?? 0,
            form: item.FormUa,
            name: item.NameFullUa,
            requiredQuantity: +medicationMetadata.treatment_dose,
          };
        }),
        latitude: PHARMACIES_LATITUDE,
        longitude: PHARMACIES_LONGITUDE,
        maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
        requireNewFormation: true,
      };

      return requestBody;
    } else {
      const morion_items: LiloDrugSearchResponse[] = medicationMetadata.trademarks ? JSON.parse(medicationMetadata.trademarks) : [];
      const requestBody: MedicationBasketRequest = {
        uniqueId: crypto.randomUUID(),
        products: morion_items.map(item => ({
          prescriptionId: prescriptionId,
          code: item.code,
          identifier: item.identifier,
          substanceQuantity: item.substanceQuantity,
          quantityInPackage: item.quantityInPackage ?? 0,
          form: item.formUa,
          name: item.nameFullUa,
          requiredQuantity: +medicationMetadata.treatment_dose,
        })),
        latitude: PHARMACIES_LATITUDE,
        longitude: PHARMACIES_LONGITUDE,
        maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
        requireNewFormation: true,
      };

      return requestBody;
    }
  }

  protected openManagerModal() {
    const data = {
      prescription: this.prescription(),
      prescriptionType: this.prescription().type,
      memberId: this.memberId() ?? this.getMemberIdFromRoute(),
      guaranteeApplicationId: this.prescription().guaranteeApplicationId || this.prescription()['guaranteeId'] || null,
      pharmaciesCount: this.pharmaciesCount(),
    };

    if (this.prescription().isFreeForm) {
      this.openFreeFormManager(data);
      return;
    }

    let componentToOpen: ComponentType<unknown>;

    switch (this.prescription().type) {
      case PrescriptionTypes.Drugs:
        componentToOpen = MedicationPrescriptionFormModalComponent;
        break;
      case PrescriptionTypes.PersonalDoctorAppointment:
        componentToOpen = PersonalDoctorPrescriptionFormModalComponent;
        break;
      default:
        componentToOpen = ServicePrescriptionFormModalComponent;
        break;
    }

    const dialogRef = this.dialog.open(componentToOpen, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data,
    });
    this.handleManagerClose(dialogRef);
  }

  private openFreeFormManager(data: Record<string, unknown>): void {
    const dialogRef = this.dialog.open(FreeFormModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: { ...data, mode: 'managed', isFreeForm: true },
    });
    this.handleManagerClose(dialogRef);
  }

  private handleManagerClose(dialogRef: MatDialogRef<unknown>): void {
    dialogRef
      .afterClosed()
      .pipe(
        tap(() => this.itemLoading.set(true)),
        switchMap(result => {
          if (result) {
            this.store.dispatch(savePrescription());
            this.store.dispatch(refreshPrescriptionsData({}));
            this.prescriptionUpdated.emit(true);
          }

          return of(null);
        }),
        take(1)
      )
      .subscribe({
        complete: () => this.itemLoading.set(false),
      });
  }

  protected getMemberIdFromRoute() {
    return this.route.snapshot.params['id'];
  }

  protected handlePrescription(guarantee: boolean) {
    this.itemLoading.set(true);
    this.dialog
      .open(PrescriptionDialogComponent, {
        minWidth: '600px',
        width: '720px',
        data: {
          isGuaranteed: guarantee,
          franchise: this.prescription().franchise ?? 0,
          categories: this.prescription().categories ?? [],
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(formValue => {
        if (formValue) {
          const combinedReason = combineReasons({
            editReason: '',
            statusReason: formValue.declineReason,
          });
          this.updatePrescriptionGuaranteeStatus(formValue.approve, combinedReason, false, null, formValue.categories);
        } else this.itemLoading.set(false);
      });
  }

  protected deleteDraftPrescription(id: string) {
    this.store.dispatch(deletePrescriptionDraft({ id }));
  }

  protected toggleHistoricalInfo() {
    this.showHistoricalChanges.update(value => !value);
    if (this.showHistoricalChanges()) this.showSimilarPrescriptions.set(false);
  }

  protected toggleSimilarPrescriptions() {
    this.checkSimilar();
    this.showSimilarPrescriptions.update(value => !value);
    if (this.showSimilarPrescriptions()) this.showHistoricalChanges.set(false);
  }

  protected checkSimilar() {
    if (this.similarChecked()) return;
    this.similarChecked.set(true);
    this.similarLoading.set(true);

    this.similarPrescriptionsService
      .getSimilarPrescriptions(this.prescription(), this.memberId() ?? this.getMemberIdFromRoute())
      .pipe(take(1))
      .subscribe({
        next: results => {
          this.similarPrescriptions.set(results);
          this.similarLoading.set(false);
        },
        error: () => this.similarLoading.set(false),
      });
  }

  private updatePrescriptionGuaranteeStatus(
    approve: boolean,
    reason: string,
    completedInAppointment: boolean = false,
    completionReason: string | null = null,
    categories: PrescriptionCategory[] = this.prescription().categories ?? []
  ) {
    const status = approve ? PrescriptionStatus.Guaranteed : PrescriptionStatus.NotGuaranteed;

    return this.prescriptionsService
      .updatePrescription(
        this.memberId() ?? this.getMemberIdFromRoute(),
        this.prescription().guaranteeApplicationId,
        this.prescription().id,
        prescriptionToRequestBody(this.prescription(), status, reason, completedInAppointment, completionReason, categories)
      )
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch(savePrescription());
        this.store.dispatch(refreshPrescriptionsData({}));
        this.prescriptionUpdated.emit(true);
        this.itemLoading.set(false);
      });
  }

  private getPersonalDoctorName() {
    this.doctorsService
      .getPersonalDoctors()
      .pipe(
        tap(doctors => {
          const doctorProfile = doctors.items.find(doctor => doctor.id === (this.prescription().metadata as PersonalDoctorMetadata).doctor_id);

          this.doctorName.set(doctorProfile ? doctorProfile.fullName : '');
        }),
        take(1)
      )
      .subscribe();
  }

  protected openAppointmentsDetailsDialog() {
    const dialogRef = this.dialog.open(AppointmentsDetailsModalComponent, {
      data: {
        memberId: this.memberId() ?? this.getMemberIdFromRoute(),
        prescriptionData: this.prescription(),
        plannings: this.prescription().planning || <PrescriptionPlanning[]>[],
      },
      maxHeight: '90vh',
      minWidth: '800px',
      disableClose: true,
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  protected getFranchiseLabel(percentage: number): string {
    if (percentage === 0) return 'Клієнт отримає послугу безкоштовно';
    else return `Клієнт повинен сплатити ${percentage}% вартості послуги`;
  }

  protected async openCreateGuaranteeFlow() {
    if (this.isInMultiStepperScope) return;

    const modalData: MultiStepperModalData = {
      userId: this.prescription().userId,
      memberId: this.memberId() ?? this.getMemberIdFromRoute(),
      appointmentName: this.prescription().name,
      appointmentType: this.getAppointmentType(),
      appointmentId: this.actualPrescriptionPlanning()?.appointmentId,
      prescriptions: [
        { id: this.prescription().id, franchise: this.prescription().franchise || 0, name: this.prescription().name, reservedPrice: this.prescription().reservedPrice },
      ],
    };

    const { MultiStepperModalComponent } = await import('../../../components/multi-stepper-modal/multi-stepper-modal.component');

    this.dialog
      .open(MultiStepperModalComponent, {
        minWidth: '1000px',
        maxHeight: '90vh',
        disableClose: false,
        data: modalData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.store.dispatch(savePrescription());
          this.store.dispatch(refreshPrescriptionsData({}));
          this.prescriptionUpdated.emit(true);
        }
      });
  }

  protected createNewAppointment() {
    const modalData: CreateAppointmentModalData = {
      type: CreateAppointmentModalType.ManualCreation,
      userId: this.prescription().userId,
      appointmentType: this.getAppointmentType(),
      appointmentName: this.prescription().name,
      eventPlace: AppointmentEventPlaceType.Hospital,
      prescriptions: [
        {
          id: this.prescription().id,
          franchise: this.prescription().franchise || 0,
        },
      ],
    };

    const dialogRef = this.dialog.open(CreateAppointmentModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  protected bindToExistingAppointment() {
    switch (this.prescription().type) {
      case PrescriptionTypes.LaboratoryService:
        this.bindToExistingLaboratoryOrder();
        break;
      default:
        this.bindToExistingVisit();
        break;
    }
  }

  private bindToExistingVisit() {
    const modalData: CreateAppointmentModalData = {
      type: CreateAppointmentModalType.BindToExisting,
      userId: this.prescription().userId,
      appointmentType: this.getAppointmentType(),
      appointmentName: this.prescription().name,
      eventPlace: AppointmentEventPlaceType.Hospital,
      prescriptions: [
        {
          id: this.prescription().id,
          franchise: this.prescription().franchise || 0,
        },
      ],
    };

    const dialogRef = this.dialog.open(CreateAppointmentModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  private bindToExistingLaboratoryOrder() {
    const modalData: CreateLaboratoryOrderModalData = {
      type: CreateAppointmentModalType.BindToExisting,
      userId: this.prescription().userId,
      appointmentType: AppointmentTypes[this.prescription().type],
      appointmentName: this.prescription().name,
      eventPlace: AppointmentEventPlaceType.Hospital,
      prescriptions: [
        {
          id: this.prescription().id,
          franchise: this.prescription().franchise || 0,
        },
      ],
    };

    const dialogRef = this.dialog.open(CreateLaboratoryOrderModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  protected cancelAppointment() {
    const planning = this.findCancellablePlanning();
    if (!planning) return;

    const modalData: CancelAppointmentModalData = {
      id: planning.id,
      prescriptionId: this.prescription().id,
      appointmentName: this.prescription().name,
      prescription: this.prescription(),
      partnerName: planning.partnerName,
      address: planning.locationAddress,
      startDate: planning.dateStart,
    };

    const dialogRef = this.dialog.open(CancelAppointmentModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  private findCancellablePlanning(): PrescriptionPlanning | null {
    const plannings = this.prescription().planning;
    if (!plannings?.length) return null;

    const isLaboratory = this.prescription().type === PrescriptionTypes.LaboratoryService;
    const groupKey: keyof PrescriptionPlanning = isLaboratory ? 'orderId' : 'appointmentId';

    const cancellableStatuses = new Set<PrescriptionPlanningStatus>([
      PrescriptionPlanningStatus.Planned,
      PrescriptionPlanningStatus.PendingCreation,
      PrescriptionPlanningStatus.PendingReschedule,
      PrescriptionPlanningStatus.PendingCancellation,
      PrescriptionPlanningStatus.Failed,
    ]);

    // Group plannings by appointmentId or orderId
    const groups = new Map<string, PrescriptionPlanning[]>();
    for (const planning of plannings) {
      const key = planning[groupKey] as string;
      if (!key) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(planning);
    }

    // Sort each group by lastUpdateDate desc, keep only groups whose latest planning is cancellable
    // Then sort those groups by latest lastUpdateDate desc and return the latest planning from the most recent group
    const byLastUpdate = (a: PrescriptionPlanning, b: PrescriptionPlanning) => new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime();
    const cancellableGroups = [...groups.values()]
      .map(group => group.sort(byLastUpdate))
      .filter(group => cancellableStatuses.has(group[0].planningStatus))
      .sort((a, b) => byLastUpdate(a[0], b[0]));

    return cancellableGroups.length ? cancellableGroups[0][0] : null;
  }

  private findRelatedLaboratoryPrescriptions(planning: PrescriptionPlanning): Prescription[] {
    const orderIdentifier = planning.orderNumber || planning.orderId;
    if (!orderIdentifier || !this.allPrescriptions().length) return [this.prescription()];

    const guaranteeApplicationId = this.prescription().guaranteeApplicationId;
    const relatedPrescriptions = this.allPrescriptions().filter(prescription => {
      if (prescription.guaranteeApplicationId !== guaranteeApplicationId) return false;
      if (prescription.type !== PrescriptionTypes.LaboratoryService) return false;

      return prescription.planning?.some(
        p =>
          p.orderId === planning.orderId ||
          p.orderNumber === planning.orderNumber ||
          (p.orderNumber && p.orderNumber === orderIdentifier) ||
          (p.orderId && p.orderId === orderIdentifier)
      );
    });

    return relatedPrescriptions.length > 0 ? relatedPrescriptions : [this.prescription()];
  }

  private getAppointmentType(): AppointmentTypes {
    if (this.prescription().type === this.prescriptionTypes.Appointment) return AppointmentTypes.Appointment;
    else if (this.prescription().type === this.prescriptionTypes.InstrumentalDiagnostic) return AppointmentTypes.InstrumentalDiagnostic;
    else return AppointmentTypes.Appointment;
  }

  protected openModalByPrescriptionType(type: PrescriptionTypes) {
    switch (type) {
      case PrescriptionTypes.Appointment:
      case PrescriptionTypes.InstrumentalDiagnostic:
        this.createNewAppointment();
        break;
      case PrescriptionTypes.LaboratoryService:
        this.createNewLaboratoryOrder();
        break;
      case PrescriptionTypes.Drugs:
        this.openPharmacyOrderModal();
        break;
      default:
        console.warn('No modal configured for prescription type:', this.prescription().type);
        break;
    }
  }

  // TODO: DRAFT / TEST-ONLY — gated by the `PHARMACY_ORDER_ENABLED` token (disabled on the production build).
  protected async openPharmacyOrderModal() {
    const { PharmacyOrderModalComponent } = await import('../../../components/pharmacy-order-modal/pharmacy-order-modal.component');

    this.dialog
      .open(PharmacyOrderModalComponent, {
        width: '90vw',
        maxWidth: '1100px',
        maxHeight: '90vh',
        disableClose: false,
        data: {
          prescription: this.prescription(),
          memberId: this.memberId() ?? this.getMemberIdFromRoute(),
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.store.dispatch(savePrescription());
          this.store.dispatch(refreshPrescriptionsData({}));
          this.prescriptionUpdated.emit(true);
        }
      });
  }

  protected createNewLaboratoryOrder() {
    const modalData: CreateLaboratoryOrderModalData = {
      type: CreateAppointmentModalType.ManualCreation,
      userId: this.prescription().userId,
      appointmentType: AppointmentTypes[this.prescription().type],
      appointmentName: this.prescription().name,
      eventPlace: AppointmentEventPlaceType.Hospital,
      prescriptions: [
        {
          id: this.prescription().id,
          franchise: this.prescription().franchise || 0,
        },
      ],
    };

    const dialogRef = this.dialog.open(CreateLaboratoryOrderModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  protected cancelLaboratoryOrder() {
    const planning = this.findCancellablePlanning();
    if (!planning) return;

    const relatedPrescriptions = this.findRelatedLaboratoryPrescriptions(planning);
    const modalData: CancelLaboratoryOrderModalData = {
      id: planning.id,
      prescriptionId: this.prescription().id,
      orderNumber: planning.orderNumber || planning.orderId,
      relatedPrescriptions: relatedPrescriptions,
      partnerName: planning.partnerName,
      address: planning.locationAddress,
      startDate: planning.dateStart,
    };
    const dialogRef = this.dialog.open(CancelLaboratoryOrderModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: modalData,
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe();
  }

  protected completeAppointment() {
    const planning = this.actualPrescriptionPlanning();
    if (!planning || !planning.appointmentId) return;

    this.itemLoading.set(true);

    this.createAppointmentService
      .updateAppointmentStatus(planning.appointmentId, AppointmentStatus.Completed)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(refreshPrescriptionsData({}));
          this.prescriptionUpdated.emit(true);
          this.itemLoading.set(false);
        },
        error: () => {
          console.error('Failed to complete appointment');
          this.itemLoading.set(false);
        },
      });
  }

  protected completeLaboratoryOrder() {
    const planning = this.actualPrescriptionPlanning();
    if (!planning || !planning.appointmentId) return;

    this.itemLoading.set(true);

    this.createAppointmentService
      .updateAppointmentStatus(planning.appointmentId, AppointmentStatus.Completed)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(refreshPrescriptionsData({}));
          this.prescriptionUpdated.emit(true);
          this.itemLoading.set(false);
        },
        error: () => {
          console.error('Failed to complete laboratory order');
          this.itemLoading.set(false);
        },
      });
  }

  protected deletePrescription() {
    const dialogRef = this.dialog.open(DeletePrescriptionModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: {
        prescription: this.prescription(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(deleted => {
        if (deleted) this.prescriptionUpdated.emit(true);
      });
  }
}
